import {
	CountrySchema,
	dateFormats,
	DateObject,
	DateOfBirthSchema,
	DocumentTypeSchema,
	ExpiryDateSchema,
	isPwaMode,
	NotificationService,
	TrackingEventTypeWEB,
	useIntl
} from '@capsicum/common';
import {
	ActivityIndicator,
	Button,
	CountrySelect,
	DateInput,
	DocumentSelect,
	ExpiryDateInput,
	Image,
	Snackbar,
	TextInput,
	useIdParam,
	useRouter
} from '@capsicum/common-web';
import {
	type FieldsObject,
	type FieldType,
	setProductQuantityAtom,
	useOrderDetails,
	useProductPage
} from '@capsicum/features/fanshop/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { FormProvider, useForm } from 'react-hook-form';
import { styled, useTheme } from 'styled-components';
import { z, type ZodRawShape } from 'zod';

export type OrderDetailsProps = {
	buttonClicked: boolean;
	handleNavigate: (value: boolean) => void;
	params: { id?: string };
	setButtonClicked: (value: boolean) => void;
};

export const OrderDetails = ({ buttonClicked, handleNavigate, params, setButtonClicked }: OrderDetailsProps) => {
	const { id: paramId } = useIdParam(params, '/fan-shop/rewards');
	const [quantity] = useAtom(setProductQuantityAtom);
	const { $t } = useIntl();
	const { format } = dateFormats;
	const router = useRouter();
	const { colors } = useTheme();

	const onFailedCheckout = useCallback(
		(errorMessage: string, errorTitle?: string) => {
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={errorMessage}
						testID="orderDetails-failedCheckout-error"
						title={errorTitle}
						variant="error"
					/>
				)
			});
			router.back();
		},
		[router]
	);

	const {
		checkoutKey,
		currentFields,
		currentStepRef,
		fields,
		getDefaultValues,
		isExperienceType,
		isLoadingOrderDetails,
		isSubmitButton,
		isTicketType,
		isUnderageTicketRedeem,
		mainBlurHash,
		mainImage,
		navigateOrders,
		plannedOrder,
		setCurrentFields,
		setFields,
		setPlaceOrderPayload,
		shouldPrefillRedeemForm,
		texts,
		variant
	} = useOrderDetails({
		onFailedCheckout,
		quantity,
		variantId: paramId as string
	});

	const { sendEventAnalytics } = useProductPage({
		variantId: paramId as string
	});

	const [isPreparingData, setIsPreparingData] = useState<boolean>(true);
	const [formKey, setFormKey] = useState(new DateObject().getTime());

	type FormShape = z.infer<ReturnType<typeof getSchema>>;

	const submitForm = useCallback(() => {
		const freshFields = getFreshFields();

		saveFields(freshFields);

		if (isSubmitButton) {
			if (isUnderageTicketRedeem(freshFields)) {
				NotificationService.addNotification({
					component: (
						<Snackbar
							description={$t('rewards.fanshop.orderDetails.ageValidationFailed')}
							variant="error"
						/>
					)
				});

				return;
			}
			setPlaceOrderPayload({
				deliveryDetails: {
					...fields
				},
				plannedOrder
			});
			sendEventAnalytics(TrackingEventTypeWEB.track, {
				is_pwa_mode: isPwaMode(),
				page: 'Delivery Details'
			});
			router.push(`/fan-shop/rewards/place-order/${paramId}`);
		}

		navigateOrders('next');
	}, [
		fields,
		isSubmitButton,
		navigateOrders,
		paramId,
		plannedOrder,
		router,
		sendEventAnalytics,
		setPlaceOrderPayload
	]);

	useEffect(() => {
		if (currentStepRef.current > 1) {
			handleNavigate(false);

			if (buttonClicked) {
				navigateOrders('prev');
				setButtonClicked(false);
			}
		} else {
			handleNavigate(true);
		}
	}, [buttonClicked, currentStepRef, handleNavigate, navigateOrders, setButtonClicked]);

	const getSchema = (allFields: Array<FieldType>) => {
		const schema: ZodRawShape = {};

		allFields.forEach(field => {
			switch (field.fieldType) {
				case 'text':
					if (!field.isOptional) {
						schema[field.id] = z.string().nonempty(texts.required);
					}
					z.string();
					break;
				case 'email':
					if (!field.isOptional) {
						schema[field.id] = z.string().email().nonempty(texts.required);
					}
					z.string().email();
					break;
				case 'datetime':
					if (field.options.mustBeFutureDate) {
						schema[field.id] = ExpiryDateSchema({
							$t
						});
					}
					if (field.options.mustBePastDate) {
						schema[field.id] = DateOfBirthSchema({
							$t,
							checkAgeOfMajority: false
						});
					}
					break;
				case 'list':
					if (field.options?.isCountryList) {
						schema[field.id] = CountrySchema;
					} else if (field.options.isDocumentTypeList) {
						schema[field.id] = DocumentTypeSchema;
					}
					break;
				default:
					break;
			}
		});

		return z.object(schema);
	};

	const form = useForm<FormShape>({
		defaultValues: getDefaultValues(currentFields ?? []),
		mode: 'onChange',
		resolver: zodResolver(getSchema(currentFields ?? []))
	});

	const getFreshFields = (): FieldsObject => {
		const currentFieldsCopy = [...currentFields!];

		const values = form.getValues();

		for (const key in values) {
			const currentField = currentFieldsCopy.find(field => field.id === key);

			switch (currentField?.fieldType) {
				case 'text':
				case 'email':
				case 'list':
					currentField.value = values[key];
					break;
				case 'datetime': {
					if (values[key][9] !== 'Y') {
						const dateParts = values[key].split('-');

						const dateObject = new DateObject(
							`${dateParts[0]}-${dateParts[1]}-${dateParts[2]}T00:00:00+00:00`
						);

						currentField.value = format(dateObject);
					}
				}
			}
		}

		return {
			...fields,
			[checkoutKey]: currentFieldsCopy
		};
	};

	const saveFields = (freshFields: FieldsObject) => {
		setFields({
			...freshFields
		});
	};

	useEffect(() => {
		if (fields && Object.keys(fields)?.length && checkoutKey) {
			setCurrentFields(fields[checkoutKey as string]);
		}

		form.reset({
			...getDefaultValues(fields?.[checkoutKey as string] ?? [], shouldPrefillRedeemForm)
		});
	}, [fields, checkoutKey, shouldPrefillRedeemForm]);

	useEffect(() => {
		if (currentFields?.length) {
			setFormKey(new DateObject().getTime());

			setIsPreparingData(false);
		}
	}, [currentFields]);

	const getFormField = useCallback((field: FieldType) => {
		switch (field.fieldType) {
			case 'text':
				return (
					<TextInput
						disabled={!field.isEditable}
						label={field.name}
						name={field.id}
						placeholder={field.placeholder}
						testID={`${field.id}-textInput`}
					/>
				);
			case 'email':
				return (
					<TextInput
						disabled={!field.isEditable}
						label={field.name}
						name={field.id}
						placeholder={field.placeholder}
						testID={`${field.id}-emailInput`}
					/>
				);
			case 'datetime':
				if (field.options.mustBeFutureDate) {
					return (
						<ExpiryDateInput
							disabled={!field.isEditable}
							label={field.name}
							name={field.id}
							placeholder={field.placeholder}
							testID={`${field.id}-expireDate`}
						/>
					);
				} else if (field.options.mustBePastDate) {
					return (
						<DateInput
							disabled={!field.isEditable}
							label={field.name}
							name={field.id}
							placeholder={field.placeholder}
							testID={`${field.id}-dateOfBirth`}
						/>
					);
				} else {
					return null;
				}

			case 'list':
				if (field.options?.isCountryList) {
					return (
						<CountrySelect
							disabled={!field.isEditable}
							label={field.name}
							labelProp="name"
							name={field.id}
							placeholder={field.placeholder}
							readonly={!field.isEditable}
							tabIndex={0}
							testID={`${field.id}-country-select`}
							withoutInitialValue={field.isEditable}
							withSearch
						/>
					);
				} else if (field.options.isDocumentTypeList) {
					return (
						<DocumentSelect
							disabled={!field.isEditable}
							documentTypeProp={field.options.values!}
							label={field.name}
							name={field.id}
							placeholder={field.placeholder}
							tabIndex={0}
							testID={`${field.id}-document-type`}
						/>
					);
				} else {
					return null;
				}

			default:
				return null;
		}
	}, []);

	const titleSection = useMemo(
		() => (
			<S.PageTitle>
				<div>{texts.titleDetails}</div>
				{(isTicketType || isExperienceType) && quantity > 1 && <div>{texts.numberOfProducts}</div>}
			</S.PageTitle>
		),
		[texts.titleDetails, isTicketType, quantity, texts.numberOfProducts, isExperienceType]
	);

	const imageSection = useMemo(
		() => (
			<S.ImageContainer>
				{mainImage ? (
					<S.StyledCardImage
						alt={`${variant?.label} image`}
						blurhash={mainBlurHash}
						height={303}
						optimized
						src={mainImage}
						width={256}
					/>
				) : (
					<S.StyledCardImagePlaceholder />
				)}
			</S.ImageContainer>
		),
		[mainBlurHash, mainImage, variant?.label]
	);

	return (
		<S.PageWrapper>
			{titleSection}
			{isLoadingOrderDetails || isPreparingData ? (
				<ActivityIndicator color={colors.icons.main.enabled} style={{ alignSelf: 'center' }} />
			) : (
				<S.ProductWrapper>
					{imageSection}

					<S.ProductInfoWrapper>
						<FormProvider key={formKey} {...form}>
							{currentFields?.map((field, index) => (
								<S.InputWrapper key={`${field.id}-${index}`}>{getFormField(field)}</S.InputWrapper>
							))}
						</FormProvider>
						<S.ButtonWrapper>
							<Button
								disabled={!form.formState.isValid}
								fullWidth={isMobile}
								testID="orderDetails-submit-button"
								text={texts.redeem}
								onClick={submitForm}
							/>
						</S.ButtonWrapper>
					</S.ProductInfoWrapper>
				</S.ProductWrapper>
			)}
		</S.PageWrapper>
	);
};

namespace S {
	export const PageWrapper = styled.div`
		width: 100%;
		display: flex;
		flex-direction: column;
	`;
	export const ProductWrapper = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		width: 100%;
		display: grid;
		grid-template-columns: calc(50% - 10px) calc(50% - 10px);
		grid-column-gap: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			display: flex;
			flex-direction: column;
			margin-bottom: 70px;
		}
	`;

	export const ProductInfoWrapper = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		width: 100%;
		${({ theme }) => theme.border.radius.medium};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			flex-direction: column;
		}
	`;

	export const PageTitle = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: ${({ theme }) => theme.spacings.xxlarge} 0 ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin-left: ${({ theme }) => theme.spacings.small};
		}
	`;

	export const ImageContainer = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.medium};
		}
	`;

	export const InputWrapper = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const StyledCardImage = styled(Image)`
		object-fit: contain;
		width: 100%;
		margin: 0 auto;
		${({ theme }) =>
			theme.breakpoints.queries.builder.or(
				theme.breakpoints.queries.above.mediumDesktop,
				theme.breakpoints.queries.only.mobile
			)} {
		}
	`;

	export const ButtonWrapper = styled.div`
		float: right;

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			position: fixed;
			width: 100%;
			bottom: 0;
			left: 0;
			margin-top: 0;
			padding: ${({ theme }) => theme.spacings.small};
			background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
			display: flex;
			flex-direction: row;
			align-self: center;
		}
	`;

	export const StyledCardImagePlaceholder = styled.div`
		object-fit: contain;
		height: 254px;
		width: 256px;
		background-color: ${({ theme }) => theme.colors.skeleton.default};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			width: 100%;
		}
	`;
}
